/**
 * The page a sign-in popup comes back to.
 *
 * MSAL 5 finishes a popup sign-in by having the popup's redirect page call `broadcastResponseToMainFrame()`, which
 * sends the response to the window that opened it and closes the popup. The popup's redirect URI is this app's root,
 * so `main.tsx` asks `isAuthPopupReturn()` before it renders anything and relays instead. Only a response whose state
 * says it belongs to a popup counts: a redirect sign-in lands on the same root and must reach `handleRedirectPromise`
 * untouched.
 */

/** True when the URL carries an authorization response that MSAL asked for through a popup. */
export function isAuthPopupReturn(): boolean {
  try {
    const params = new URLSearchParams(
      `${window.location.search.replace(/^\?/, "")}&${window.location.hash.replace(/^#/, "")}`,
    );
    const state = params.get("state");
    if (!state || !(params.has("code") || params.has("error"))) return false;
    // MSAL's own state is `base64(JSON{id, meta}) [| user state]`.
    const library = state.split("|")[0]!.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(atob(library)) as {
      meta?: { interactionType?: string };
    };
    return decoded.meta?.interactionType === "popup";
  } catch {
    return false;
  }
}

/** Hand the response to the opener and close this popup. */
export async function relayAuthPopupReturn(): Promise<void> {
  const { broadcastResponseToMainFrame } =
    await import("@azure/msal-browser/redirect-bridge");
  await broadcastResponseToMainFrame();
}
